import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Panel from "../components/common/Panel";
import Seo from "../components/common/Seo";
import { apiCheckIpAccess } from "../api/backend";
import { listPosts } from "../data/blogPosts";
import { t } from "../i18n/messages";
import { localeContent } from "../utils/localeContent";
import { useAppState } from "../state/AppStateProvider";
import { buildPath } from "../utils/routing";

export default function HomePage({ locale }) {
  const navigate = useNavigate();
  const {
    walletLinked,
    balance,
    balanceSymbol,
  } = useAppState();
  const posts = listPosts().slice(0, 3);

  useEffect(() => {
    let canceled = false;

    async function checkIpAccess() {
      try {
        const access = await apiCheckIpAccess();
        if (!canceled && access?.blocked) {
          navigate(buildPath(locale, "forbidden"), { replace: true });
        }
      } catch (err) {
        // If the request was blocked due to region restriction, redirect to forbidden.
        // (The global middleware intercepts /api/access/check-ip and returns 403 before
        // the endpoint itself can respond with ok:true, so the request() fn throws with
        // regionBlocked = true instead of returning access data.)
        if (!canceled && err?.regionBlocked) {
          navigate(buildPath(locale, "forbidden"), { replace: true });
        }
        // Other failures (network error, server error) are ignored so the page stays usable.
      }
    }

    checkIpAccess();

    return () => {
      canceled = true;
    };
  }, [locale, navigate]);

  const cards = [
    {
      slug: "blog",
      title: t(locale, "navBlog"),
      desc: t(locale, "homeCardBlogDesc"),
    },
    {
      slug: "auction",
      title: t(locale, "navAuction"),
      desc: t(locale, "homeCardAuctionDesc"),
    },
    {
      slug: "market",
      title: t(locale, "navMarket"),
      desc: t(locale, "homeCardMarketDesc"),
    },
    {
      slug: "rwa",
      title: t(locale, "navRwa"),
      desc: t(locale, "homeCardRwaDesc"),
    },
    {
      slug: "settlement",
      title: t(locale, "navSettlement"),
      desc: t(locale, "homeCardSettlementDesc"),
    },
    {
      slug: "profile",
      title: t(locale, "navProfile"),
      desc: t(locale, "homeCardProfileDesc"),
    },
    {
      slug: "studio",
      title: t(locale, "navStudio"),
      desc: t(locale, "homeCardStudioDesc"),
    },
  ];

  return (
    <>
      <Seo
        locale={locale}
        title={`Chronos Arte Node | ${t(locale, "navHome")}`}
        description={t(locale, "homeDesc")}
        path={buildPath(locale, "")}
      />

      <section className="hero panel home-hero-panel">
        <div className="home-hero-atmosphere" aria-hidden="true">
          <span className="home-hero-orb home-hero-orb-a" />
          <span className="home-hero-orb home-hero-orb-b" />
        </div>

        <div className="home-hero-layout">
          <div className="home-hero-shell">
            <p className="home-hero-kicker">Chronos Arte Node</p>
            <h1>{t(locale, "homeTitle")}</h1>
            <p className="home-hero-desc">{t(locale, "homeDesc")}</p>

            <p
              className={`home-wallet-pill ${walletLinked ? "is-linked" : "is-disconnected"}`}
            >
              <span className="home-wallet-pill-dot" aria-hidden="true" />
              {t(locale, "homeWalletLink")}:{" "}
              {walletLinked
                ? t(locale, "homeConnected")
                : t(locale, "homeDisconnected")}
            </p>
          </div>

          <aside className="home-status-shell">
            <div className="home-status-grid">
              <article className="metric-card home-metric-card home-metric-card-wide">
                <span>{t(locale, "homeBalance")}</span>
                <strong>
                  {balance.toFixed(4)} {balanceSymbol}
                </strong>
              </article>
            </div>
          </aside>
        </div>
      </section>

      <Panel
        className="home-features-panel"
        title={t(locale, "homeCoreFeatures")}
        description={t(locale, "homeCoreDesc")}
      >
        <div className="card-grid home-feature-grid">
          {cards.map((item, index) => (
            <Link
              key={item.slug}
              to={buildPath(locale, item.slug)}
              className={`card-link home-feature-card ${index % 2 ? "is-alt" : ""}`}
            >
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
              <span className="home-feature-index">
                {String(index + 1).padStart(2, "0")}
              </span>
            </Link>
          ))}
        </div>
      </Panel>

      <Panel
        className="home-blog-panel"
        title={t(locale, "blogTitle")}
        description={t(locale, "blogDesc")}
      >
        <div className="card-grid home-blog-grid">
          {posts.map((post, index) => {
            const content = localeContent(post.locales, locale);
            return (
              <Link
                key={post.id}
                className={`card-link home-blog-card ${index === 0 ? "is-featured" : ""}`}
                to={buildPath(locale, `blog/${post.id}`)}
              >
                <h3>{content?.title || post.id}</h3>
                <p>{content?.excerpt || ""}</p>
                <span className="home-blog-meta">
                  {post.publishDate} · {post.readMinutes}{" "}
                  {t(locale, "blogMinutes")}
                </span>
              </Link>
            );
          })}
        </div>
      </Panel>
    </>
  );
}
